import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import {
    Card, Stack, Container, Typography, TextField, Alert, Box, Link, Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function InvestorResendVerification() {
    const [searchParams] = useSearchParams();
    const [sent, setSent] = useState(false);
    const [apiError, setApiError] = useState('');

    const ResendSchema = Yup.object().shape({
        email: Yup.string().email('Must be a valid email').required('Email is required'),
    });

    const formik = useFormik({
        initialValues: { email: searchParams.get('email') || '' },
        validationSchema: ResendSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('investor/resend-verification/', values);
                setSent(true);
            } catch (error) {
                const resp = error.response?.data;
                if (resp?.code === 'RATE_LIMITED') {
                    setApiError('Verification email was recently sent. Please try again in 5 minutes.');
                } else {
                    setSent(true); // Always show success to prevent email enumeration
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <RootStyle title="Resend Verification | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" gutterBottom>Resend Verification Email</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter your email and we'll send you a new verification link.
                            </Typography>
                        </Box>

                        {apiError && <Alert severity="error">{apiError}</Alert>}

                        {sent ? (
                            <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
                                <Alert severity="success" sx={{ width: '100%' }}>
                                    If an account with that email exists and is pending verification, a new email has been sent.
                                </Alert>
                                <Typography variant="body2" color="text.secondary">
                                    Check your inbox and follow the link to verify your email address.
                                </Typography>
                            </Stack>
                        ) : (
                            <FormikProvider value={formik}>
                                <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            type="email"
                                            {...formik.getFieldProps('email')}
                                            error={Boolean(formik.touched.email && formik.errors.email)}
                                            helperText={formik.touched.email && formik.errors.email}
                                        />
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={formik.isSubmitting}
                                        >
                                            Resend Verification Email
                                        </LoadingButton>
                                    </Stack>
                                </Form>
                            </FormikProvider>
                        )}

                        <Divider />
                        <Link component={RouterLink} to="/investor/login" variant="body2" sx={{ textAlign: 'center' }}>
                            Back to Login
                        </Link>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
